import { useState } from 'react';
import { Plus, ChevronDown, ChevronRight, Check, Circle, Clock } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { PHASE_STATUSES, GATE_TYPES, GATE_STATUSES, INPUT } from './config';
import type { ProjectPhase, ProjectTask, ProjectGate } from '../../types';
import type { TeamUser } from './hooks';

interface Props {
  phases: ProjectPhase[];
  tasks: ProjectTask[];
  gates: ProjectGate[];
  users: TeamUser[];
  userId: string;
  onToggleTask: (taskId: string, status: string) => void;
  onAssignTask: (taskId: string, assigneeId: string | null) => void;
  onCreateTask: (phaseId: string, name: string, order: number) => void;
  onCreatePhase: (name: string, order: number) => void;
  onResolveGate: (gateId: string, action: 'resolved' | 'waived', note: string | null) => void;
  onCreateGate: (name: string, gateType: string, afterPhaseId: string | null, order: number) => void;
}

export function PhaseSection({ phases, tasks, gates, users, onToggleTask, onAssignTask, onCreateTask, onCreatePhase, onResolveGate }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(phases.slice(0, 2).map(p => p.id)));
  const [addPhaseName, setAddPhaseName] = useState('');
  const [addTaskPhaseId, setAddTaskPhaseId] = useState<string | null>(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [resolveGateId, setResolveGateId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');

  const toggle = (id: string) => {
    const next = new Set(expanded);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const taskIcon = (status: string) => {
    if (status === 'completed') return <Check size={14} color={palette.green} />;
    if (status === 'in_progress') return <Clock size={14} color={palette.accentDeep} />;
    return <Circle size={14} color={palette.g300} />;
  };

  return (
    <div>
      {phases.map((phase) => {
        const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
        const completedCount = phaseTasks.filter(t => t.status === 'completed').length;
        const phGates = gates.filter(g => g.after_phase_id === phase.id);
        const ps = PHASE_STATUSES[phase.status] ?? PHASE_STATUSES.not_started;
        const isOpen = expanded.has(phase.id);

        return (
          <div key={phase.id} style={{ marginBottom: 2 }}>
            {/* Phase header */}
            <div onClick={() => toggle(phase.id)} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer',
              background: palette.g50, borderRadius: 6, border: `1px solid ${palette.g150}`,
            }}>
              <div style={{ display: 'flex', color: palette.g500 }}>
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
              {/* Timeline dot */}
              <div style={{ width: 10, height: 10, borderRadius: 5, background: phase.status === 'completed' ? palette.green : phase.status === 'in_progress' ? palette.accentDeep : palette.g300, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: palette.g800 }}>{phase.name}</span>
              <span style={{ fontSize: 11, color: palette.g500 }}>{completedCount}/{phaseTasks.length} tasks</span>
              <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500, background: ps.bg, color: ps.fg }}>{ps.label}</span>
            </div>

            {/* Tasks */}
            {isOpen && (
              <div style={{ marginLeft: 24, paddingLeft: 16, borderLeft: `2px solid ${palette.g200}`, paddingTop: 4, paddingBottom: 8 }}>
                {phaseTasks.map(task => {
                  return (
                    <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                      <button onClick={() => onToggleTask(task.id, task.status)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        {taskIcon(task.status)}
                      </button>
                      <span style={{ flex: 1, fontSize: 13, color: task.status === 'completed' ? palette.g400 : palette.g700, textDecoration: task.status === 'completed' ? 'line-through' : 'none' }}>{task.name}</span>
                      {task.estimated_hours && <span style={{ fontSize: 11, color: palette.g400 }}>{task.estimated_hours}h</span>}
                      <select value={task.assigned_to ?? ''} onChange={e => onAssignTask(task.id, e.target.value || null)}
                        style={{ fontSize: 11, padding: '2px 4px', border: `1px solid ${palette.g200}`, borderRadius: 4, color: palette.g600, background: palette.white, fontFamily: 'inherit' }}>
                        <option value="">—</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.first_name}</option>)}
                      </select>
                    </div>
                  );
                })}
                {/* Add task */}
                {addTaskPhaseId === phase.id ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                    <input style={{ ...INPUT, flex: 1, padding: '5px 8px', fontSize: 12 }} value={newTaskName} onChange={e => setNewTaskName(e.target.value)} placeholder="Task name" autoFocus onKeyDown={e => {
                      if (e.key === 'Enter' && newTaskName.trim()) { onCreateTask(phase.id, newTaskName.trim(), phaseTasks.length + 1); setNewTaskName(''); setAddTaskPhaseId(null); }
                      if (e.key === 'Escape') { setAddTaskPhaseId(null); setNewTaskName(''); }
                    }} />
                    <button onClick={() => { if (newTaskName.trim()) { onCreateTask(phase.id, newTaskName.trim(), phaseTasks.length + 1); setNewTaskName(''); setAddTaskPhaseId(null); } }} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
                  </div>
                ) : (
                  <button onClick={() => setAddTaskPhaseId(phase.id)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0', border: 'none', background: 'none', fontSize: 12, color: palette.g400, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>
                    <Plus size={12} /> Add task
                  </button>
                )}
              </div>
            )}

            {/* Gates after this phase */}
            {phGates.map(gate => {
              const gs = GATE_STATUSES[gate.status] ?? GATE_STATUSES.pending;
              const gt = GATE_TYPES[gate.gate_type] ?? GATE_TYPES.custom;
              return (
                <div key={gate.id} style={{ margin: '4px 0 4px 24px', padding: '8px 12px', borderRadius: 6, border: `1px solid ${gs.fg}20`, background: gs.bg, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: gt.fg, padding: '1px 6px', borderRadius: 3, background: gt.bg }}>{gt.label}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: palette.g700 }}>{gate.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: gs.fg }}>{gs.label}</span>
                  {(gate.status === 'pending' || gate.status === 'blocking') && (
                    resolveGateId === gate.id ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input style={{ ...INPUT, width: 120, padding: '3px 6px', fontSize: 11 }} value={resolveNote} onChange={e => setResolveNote(e.target.value)} placeholder="Note (optional)" />
                        <button onClick={() => { onResolveGate(gate.id, 'resolved', resolveNote || null); setResolveGateId(null); setResolveNote(''); }} style={{ padding: '3px 8px', borderRadius: 3, border: 'none', background: palette.green, color: palette.white, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Resolve</button>
                        <button onClick={() => { onResolveGate(gate.id, 'waived', resolveNote || null); setResolveGateId(null); setResolveNote(''); }} style={{ padding: '3px 8px', borderRadius: 3, border: 'none', background: palette.g300, color: palette.white, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Waive</button>
                      </div>
                    ) : (
                      <button onClick={() => setResolveGateId(gate.id)} style={{ padding: '3px 8px', borderRadius: 3, border: 'none', background: palette.accent, color: palette.white, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Resolve</button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Add phase */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
        <input style={{ ...INPUT, flex: 1, padding: '7px 10px', fontSize: 12.5 }} value={addPhaseName} onChange={e => setAddPhaseName(e.target.value)} placeholder="New phase name..."
          onKeyDown={e => {
            if (e.key === 'Enter' && addPhaseName.trim()) { onCreatePhase(addPhaseName.trim(), phases.length + 1); setAddPhaseName(''); }
            if (e.key === 'Escape') { setAddPhaseName(''); }
          }} />
        <button onClick={() => { if (addPhaseName.trim()) { onCreatePhase(addPhaseName.trim(), phases.length + 1); setAddPhaseName(''); } }} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: palette.accent, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={14} style={{ verticalAlign: 'middle' }} /> Phase
        </button>
      </div>
    </div>
  );
}
